import { DetailsHeader, Segments } from "components";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import PeopleActivityReport from "./Activity/PeopleActivityReport";
import UsersReport from "./Users/PeopleUsersReport";
import PeopleOverviewReport from "./Overview/PeopleOverviewReport";
import { useCallback, useRef } from "react";
import AcquisitionReport from "./Acquisition/PeopleAcquisitionReport";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import cn from "classnames";
import { Dropdown } from "semantic-ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { connect, type ConnectedProps } from "react-redux";
import { type RootState } from "features/Application/globaltypes/redux";

import styles from "../Content/contentOverviewReports.module.scss";
import { AddOnIds, RouteNames } from "enums";
import useHasAddOns from "features/Library/Common/Hooks/useHasAddOns";

const pages = [
  { to: "overview", label: "Overview" },
  { to: "activity", label: "Activity" },
  { to: "acquisition", label: "Acquisition" },
  { to: "users", label: "Users" },
];

type OwnProps = {
  usesDropdownMenuNavigation?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
  disableLinks?: boolean;
};

type Props = OwnProps & PropsFromRedux;

const ReportingPeople = ({
  usesDropdownMenuNavigation,
  childAccountId,
  hasCreateAccountsAddOn,
  disableLinks,
  accountId,
}: Props) => {
  const isPeopleReportingEnabled = !!useFeatureFlag(FeatureFlags.PeopleReports);
  const rootRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const hasCreateAccountsOrMarketplace = useHasAddOns(accountId, [AddOnIds.CreateAccounts, AddOnIds.Marketplace], "or");
  const reportTitle = hasCreateAccountsOrMarketplace ? "Customers" : "People";
  const reportBasePath = hasCreateAccountsOrMarketplace ? "customers" : "people";

  const scrollToTop = useCallback(() => {
    rootRef.current?.scrollTo({ top: 0, left: 0 });
  }, []);

  const renderDropdownMenuNavigation = useCallback(() => {
    const currentPage = pages.find((p) => location.pathname.includes(`/${p.to}`)) ?? pages[0];

    return (
      <Dropdown
        className={styles.overviewDropdown}
        text={currentPage.label}
        icon={<FontAwesomeIcon className={styles.icon} icon={faChevronDown} />}
        floating
      >
        <Dropdown.Menu>
          {pages.map((page) => (
            <Dropdown.Item
              key={page.to}
              active={currentPage.to === page.to}
              onClick={() => {
                navigate(`../${page.to}`, { relative: "path" });
              }}
              text={page.label}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }, [location.pathname, navigate]);

  const renderHeader = useCallback(() => {
    if (usesDropdownMenuNavigation) {
      return renderDropdownMenuNavigation();
    }
    return null;
  }, [usesDropdownMenuNavigation, renderDropdownMenuNavigation]);

  if (!isPeopleReportingEnabled) {
    return <ReportUnavailable />;
  }

  const renderTabNavigation = (
    <>
      <DetailsHeader title={reportTitle} showReportLastUpdated />
      <Segments to={`/reporting/${reportBasePath}`}>
        {pages.map((page) => (
          <Segments.Segment key={page.to} {...page} />
        ))}
      </Segments>
    </>
  );

  return (
    <div
      className={cn("people-reporting-root", {
        "nested-content": !usesDropdownMenuNavigation,
        [styles.rootDropdownMenuNavigation]: usesDropdownMenuNavigation,
      })}
      ref={rootRef}
    >
      {!usesDropdownMenuNavigation && renderTabNavigation}
      <div className="reportBody">
        <Routes>
          <Route
            path="overview"
            element={
              <PeopleOverviewReport
                scrollToTop={scrollToTop}
                usesDropdownMenuNavigation={usesDropdownMenuNavigation}
                renderHeader={renderHeader}
                childAccountId={childAccountId}
                hasCreateAccountsAddOn={hasCreateAccountsAddOn}
                disableLinks={disableLinks}
              />
            }
          />
          <Route
            path="activity"
            element={
              <PeopleActivityReport
                usesDropdownMenuNavigation={usesDropdownMenuNavigation}
                renderHeader={renderHeader}
                childAccountId={childAccountId}
                hasCreateAccountsAddOn={hasCreateAccountsAddOn}
                disableLinks={disableLinks}
              />
            }
          />
          <Route
            path="acquisition"
            element={
              <AcquisitionReport
                usesDropdownMenuNavigation={usesDropdownMenuNavigation}
                renderHeader={renderHeader}
                childAccountId={childAccountId}
                hasCreateAccountsAddOn={hasCreateAccountsAddOn}
                disableLinks={disableLinks}
              />
            }
          />
          <Route
            path="users"
            element={
              <UsersReport
                usesDropdownMenuNavigation={usesDropdownMenuNavigation}
                renderHeader={renderHeader}
                childAccountId={childAccountId}
                hasCreateAccountsAddOn={hasCreateAccountsAddOn}
                disableLinks={disableLinks}
              />
            }
          />
          <Route
            path="*"
            element={
              <Navigate
                to={usesDropdownMenuNavigation ? "../overview" : `/${RouteNames.reporting}/${reportBasePath}/overview`}
                replace
              />
            }
          />
        </Routes>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ReportingPeople);
export default ConnectedComponent;
